/**
 * Who this workspace's emails go out as.
 *
 * SyncSuite authenticates the *domain* of a From address (SPF/DKIM live on
 * the domain you verified in Settings → Services → Email). The local part,
 * the display name, and Reply-To are all free-form. Your SyncSuite login
 * email has nothing to do with it.
 *
 * One fetch, shared by the Settings panel and the inbox composer, so both
 * always agree about what a send will use — and so the user can read the
 * answer instead of sending a test email to find out.
 */

import { useCallback, useEffect, useState } from "react";
import { api } from "./api";

export interface EmailSendingConfig {
  /**
   * False = the backend couldn't reach the platform, so nothing below is
   * trustworthy. Never render a "no verified domain" warning on `known:
   * false` — that's a network blip, not a misconfiguration, and telling
   * the user their setup is broken when it isn't is worse than silence.
   */
  known: boolean;
  verified_domains: string[];
  pending_domains: { hostname: string; status: string }[];
  /** A verified domain AND a mailing address on file. */
  can_send: boolean;
  /**
   * Whether SyncSuite has this workspace's physical mailing address.
   * Required before any email sends — personal mail doesn't display it,
   * but the platform refuses the send without it. It's also what goes in
   * the marketing footer.
   */
  address_on_file: boolean;
  platform_default_sender: string | null;
  configured_from_email: string | null;
  configured_from_name: string | null;
  /** null when nothing is configured, else whether that domain is verified. */
  configured_domain_verified: boolean | null;
  configured_domain: string | null;
  effective: {
    from: string | null;
    from_name: string | null;
    reply_to: string | null;
  };
}

/** "Adrian De Leon <info@brand.com>", or just the address when unnamed. */
export function formatSender(name: string | null | undefined, address: string | null | undefined): string {
  if (!address) return name?.trim() || "";
  const n = name?.trim();
  return n ? `${n} <${address}>` : address;
}

export function emailDomainOf(address: string | null | undefined): string | null {
  if (!address) return null;
  const at = address.lastIndexOf("@");
  if (at <= 0 || at === address.length - 1) return null;
  return address.slice(at + 1).toLowerCase();
}

/**
 * Deep link to the platform page where sending domains are verified.
 * Returns null on a custom domain, where the CRM has no reliable way to
 * know the workspace slug — callers fall back to naming the page in prose
 * rather than rendering a link that might 404.
 */
export function syncsuiteEmailSettingsUrl(): string | null {
  if (typeof window === "undefined") return null;
  const { hostname, origin, pathname } = window.location;
  if (!/(^|\.)syncsuite\.co$/i.test(hostname)) return null;
  const workspace = pathname.split("/").filter(Boolean)[0];
  if (!workspace) return null;
  return `${origin}/${workspace}/services?open=email`;
}

/**
 * Fetch the sending config. Shared by every surface that needs it.
 * `refresh()` bypasses the backend's 60s cache — the Settings panel calls
 * it after the user has gone off to verify a domain.
 */
export function useEmailSending(enabled = true) {
  const [config, setConfig] = useState<EmailSendingConfig | null>(null);
  const [loading, setLoading] = useState(enabled);

  const fetchConfig = useCallback(async (force = false) => {
    setLoading(true);
    try {
      const d = await api.get<EmailSendingConfig>(
        "/settings/email-sending",
        force ? { refresh: "1" } : undefined,
      );
      setConfig(d);
      return d;
    } catch {
      // Non-fatal everywhere it's used: the composer just omits the From
      // line, Settings shows its plain input. Sending is unaffected.
      setConfig(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) { setLoading(false); return; }
    void fetchConfig(false);
  }, [enabled, fetchConfig]);

  return { config, loading, refresh: () => fetchConfig(true) };
}
